import React, { useState, useEffect } from 'react';
import appInsightsService from '../services/appInsights';
import {
  getAppInsightsConnectionString,
  getAppInsightsInstrumentationKey,
  getConfiguredNodeEnv,
  getServerUrl
} from '../config/runtimeConfig';

const ApplicationInsightsDiagnostic = () => {
  const [status, setStatus] = useState({});
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = () => {
    const currentStatus = {
      initialized: appInsightsService.initialized,
      hasAppInsights: !!appInsightsService.appInsights,
      hasCore: !!appInsightsService.appInsights?.core,
      environment: process.env.NODE_ENV,
      configVars: {
        hasConnectionString: !!getAppInsightsConnectionString(),
        hasInstrumentationKey: !!getAppInsightsInstrumentationKey(),
        serverUrl: getServerUrl(),
        configuredNodeEnv: getConfiguredNodeEnv()
      },
      timestamp: new Date().toISOString()
    };
    setStatus(currentStatus);
  };

  const testConfigFetch = async () => {
    setLoading(true);
    const result = {
      test: 'Runtime Configuration Fetch',
      timestamp: new Date().toISOString(),
      success: false,
      details: {}
    };

    try {
      const serverUrl = getServerUrl();

      const url = `${serverUrl}/app-insights/config`;
      
      result.details.requestUrl = url;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      result.details.status = response.status;
      result.details.statusText = response.statusText;

      if (response.ok) {
        const config = await response.json();
        result.success = true;
        result.details.config = config;
      } else {
        result.details.error = `HTTP ${response.status}: ${response.statusText}`;
      }
    } catch (error) {
      result.details.error = error.message;
    }

    setTestResults(prev => [result, ...prev]);
    setLoading(false);
  };

  const testTelemetry = async () => {
    setLoading(true);
    const result = {
      test: 'Telemetry Sending',
      timestamp: new Date().toISOString(),
      success: false,
      details: {}
    };

    try {
      const testId = `test-${Date.now()}`;
      
      // Test trace
      appInsightsService.trackTrace(`Test trace message ${testId}`, 1, {
        testId: testId,
        testType: 'diagnostic',
        source: 'diagnostic-page'
      });

      // Test event
      appInsightsService.trackEvent('DiagnosticTest', {
        testId: testId,
        action: 'manual-test'
      });

      // Test metric
      appInsightsService.trackMetric('DiagnosticTestMetric', Math.random() * 100, {
        testId: testId
      });

      // Try to flush
      if (appInsightsService.flush) {
        appInsightsService.flush();
      }

      result.success = true;
      result.details.testId = testId;
      result.details.message = 'Telemetry sent successfully';
    } catch (error) {
      result.details.error = error.message;
    }

    setTestResults(prev => [result, ...prev]);
    setLoading(false);
  };

  const reinitialize = async () => {
    setLoading(true);
    const result = {
      test: 'Reinitialization',
      timestamp: new Date().toISOString(),
      success: false,
      details: {}
    };

    try {
      // Reset the service
      appInsightsService.initialized = false;
      appInsightsService.appInsights = null;

      // Try runtime configuration
      const serverUrl = getServerUrl();

      const response = await fetch(`${serverUrl}/app-insights/config`);
      
      if (response.ok) {
        const config = await response.json();
        
        if (config.configured) {
          let initResult = false;
          if (config.connectionString) {
            initResult = appInsightsService.initialize(config.connectionString);
            result.details.method = 'connectionString';
          } else if (config.instrumentationKey) {
            initResult = appInsightsService.initialize(config.instrumentationKey);
            result.details.method = 'instrumentationKey';
          }
          
          result.success = initResult;
          result.details.initialized = appInsightsService.initialized;
        } else {
          result.details.error = 'No configuration available from backend';
        }
      } else {
        result.details.error = `HTTP ${response.status}: ${response.statusText}`;
      }
    } catch (error) {
      result.details.error = error.message;
    }

    setTestResults(prev => [result, ...prev]);
    checkStatus();
    setLoading(false);
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h2>🔍 Application Insights Diagnostic</h2>
      
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
        <h3>Current Status</h3>
        <pre>{JSON.stringify(status, null, 2)}</pre>
        <button onClick={checkStatus} style={{ marginTop: '10px' }}>
          Refresh Status
        </button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Diagnostic Tests</h3>
        <div style={{ marginBottom: '10px' }}>
          <button 
            onClick={testConfigFetch} 
            disabled={loading}
            style={{ marginRight: '10px', padding: '8px 16px' }}
          >
            Test Runtime Config Fetch
          </button>
          <button 
            onClick={testTelemetry} 
            disabled={loading}
            style={{ marginRight: '10px', padding: '8px 16px' }}
          >
            Test Telemetry Sending
          </button>
          <button 
            onClick={reinitialize} 
            disabled={loading}
            style={{ marginRight: '10px', padding: '8px 16px' }}
          >
            Reinitialize from Backend
          </button>
        </div>
        {loading && <p>⏳ Running test...</p>}
      </div>

      <div>
        <h3>Test Results</h3>
        {testResults.length === 0 ? (
          <p>No tests run yet</p>
        ) : (
          testResults.map((result, index) => (
            <div 
              key={index} 
              style={{ 
                marginBottom: '15px', 
                padding: '10px', 
                backgroundColor: result.success ? '#e8f5e8' : '#ffe8e8',
                borderRadius: '5px',
                border: `1px solid ${result.success ? '#4caf50' : '#f44336'}`
              }}
            >
              <h4>{result.success ? '✅' : '❌'} {result.test}</h4>
              <p><strong>Time:</strong> {result.timestamp}</p>
              <pre style={{ fontSize: '12px' }}>{JSON.stringify(result.details, null, 2)}</pre>
            </div>
          ))
        )}
      </div>

      <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '5px' }}>
        <h3>💡 Troubleshooting Tips</h3>
        <ul>
          <li>Check if backend is running and accessible</li>
          <li>Verify CORS configuration allows frontend requests</li>
          <li>Check network tab for failed requests</li>
          <li>Look for Application Insights configuration in backend logs</li>
          <li>Ensure environment variables are set in production</li>
        </ul>
      </div>
    </div>
  );
};

export default ApplicationInsightsDiagnostic;
